import { Directive, inject } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { CloseFunction, Dialog, DismissFunction } from './dialog';

@Directive()
export abstract class DialogComponent<Input = any, Output = any> implements Dialog<Input, Output> {
  private onClose: (result: Output) => void;
  private onDismiss: (reason: any) => void;

  /**
   * This is only here so typescript can infer the type of the generic argument.
   *
   * Reasoning: Typescript can infer types only if they are used. The problem with the generic type Input is that
   * the component should implement it (since it is an interface defining the input properties of the dialog). Since
   * that is not possible (TS2422: A class can only implement an object type or intersection of object types with statically known members.)
   * we use that workaround to enable the inference of Input.
   *
   * See answer here: https://github.com/microsoft/TypeScript/issues/360#issuecomment-52518667
   */
  '__element type'?: Input;

  protected ngbActiveModal: NgbActiveModal;

  constructor(ngbActiveModal?: NgbActiveModal) {
    this.ngbActiveModal = ngbActiveModal ?? inject(NgbActiveModal);
  }

  /**
   * Closes the modal with an optional `result` value.
   */
  public close(result: Output): void {
    this.emitClose(result);

    this.ngbActiveModal.close();
  }

  /**
   * Dismisses the modal with an optional `reason` value.
   */
  public dismiss(reason?: any): void {
    this.emitDismiss(reason);

    this.ngbActiveModal.close();
  }

  /**
   * Resolve the promise without closing the dialog. This way the component that opened the dialog can handle that.
   *
   *  @param result
   */
  public emitClose(result: Output): void {
    this.onClose(result);
  }

  /**
   * Reject the promise without closing the dialog. This way the component that opened the dialog can handle that.
   *
   * @param reason
   */
  public emitDismiss(reason?: any): void {
    this.onDismiss(reason);
  }

  /**
   * Called when the dialog was dismissed externally (e.g. by pressing escape).
   */
  public onDismissed(reason?: any): void {
    this.onDismiss(reason);
  }

  public setCallbacks(onClose: CloseFunction<Output>, onDismiss: DismissFunction): void {
    this.onClose = onClose;
    this.onDismiss = onDismiss;
  }
}
